import { Theme, display, height, margin } from '@mui/system'
import { makeStyles } from '@mui/styles'
import { FONTS, SPACES } from 'src/constants'

interface IStyleProps {
  isRoundImage?: boolean
  disabled?: boolean
  disableClickable?: boolean
  aiGenerated?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  bigContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    padding: `${SPACES.xs} ${SPACES.xxl} ${SPACES.xxl}`,
    border: `1px solid ${theme.palette.custom.grey}`,

    [theme.breakpoints.down('tablet')]: {
      width: '100%',
      height: 'auto',

      borderRadius: 10,
    },

    [theme.breakpoints.up('tablet')]: {
      borderRadius: 15,

      minWidth: 335,
    },
  },
  selectionList: {
    '&&': {
      maxWidth: '100%',
      height: '100%',

      display: 'flex',
      flexDirection: 'column',
      alignContent: 'center',
      gap: '10px',
    },
  },
  text: {
    color: theme.palette.custom.grey2,
    marginBottom: SPACES.xxxs,

    [theme.breakpoints.down('tablet')]: {
      fontSize: FONTS.SIZES.s,
    },
    [theme.breakpoints.up('tablet')]: {
      fontSize: FONTS.SIZES.xs,
    },
  },
  textCounter: {
    marginBottom: SPACES.s,

    fontSize: FONTS.SIZES.xs,
    lineHeight: 1.4,
    color: theme.palette.custom.darkBlue2,
    textTransform: 'lowercase',
  },
  selectionWrapper: (props) => ({
    '&&': {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      backgroundColor: props.aiGenerated
        ? theme.palette.custom.lightGreen
        : props.disabled
          ? theme.palette.custom.greyTransparent
          : theme.palette.custom.white,
      justifyContent: 'space-between',

      width: '100%',

      wordWrap: 'break-word',
      border: `1px solid ${theme.palette.custom.grey}`,

      textDecoration: 'none',

      [theme.breakpoints.down('tablet')]: {
        margin: `${SPACES.xxxs} 0`,

        borderRadius: 10,
      },

      [theme.breakpoints.up('tablet')]: {
        margin: `${SPACES.xs} 0`,

        borderRadius: 15,
      },

      // cursor: props.disableClickable ? 'auto' : 'pointer',
      transition: 'background-color 0.3s ease-in-out',
    },
  }),
  selectionAddWrapper: (props) => ({
    '&&': {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      backgroundColor: props.disabled
        ? theme.palette.custom.greyTransparent
        : theme.palette.custom.white,
      justifyContent: 'space-between',

      width: '100%',

      wordWrap: 'break-word',
      border: `1px solid ${theme.palette.custom.grey}`,

      textDecoration: 'none',

      [theme.breakpoints.down('tablet')]: {
        // margin: `${SPACES.xxxs} 0`,
        padding: SPACES.xs,
        borderRadius: 10,
      },

      [theme.breakpoints.up('tablet')]: {
        // margin: `${SPACES.xs} 0`,
        padding: `${SPACES.xxl} ${SPACES.xxl} ${SPACES.xl}`,
        borderRadius: 15,
      },

      cursor: props.disableClickable ? 'auto' : 'pointer',
      transition: 'background-color 0.3s ease-in-out',
    },
  }),
  linkWrapper: (props) => ({
    '&&': {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: props.aiGenerated
        ? theme.palette.custom.lightGreen
        : props.disabled
          ? theme.palette.custom.greyTransparent
          : theme.palette.custom.white,
      justifyContent: 'space-between',

      width: '100%',

      wordWrap: 'break-word',

      textDecoration: 'none',

      [theme.breakpoints.down('tablet')]: {
        margin: `${SPACES.xxxs} 0`,

        padding: SPACES.xs,

        borderRadius: 10,
      },

      [theme.breakpoints.up('tablet')]: {
        padding: `${SPACES.xxl} ${SPACES.xxl} ${SPACES.xl}`,

        borderRadius: 15,
      },

      cursor: props.disableClickable ? 'auto' : 'pointer',
    },
  }),
  selectionItem: {
    '&&': {
      display: 'flex',
      alignItems: 'center',

      width: '100%',

      wordWrap: 'break-word',

      [theme.breakpoints.down('tablet')]: {
        gap: SPACES.xs,
      },

      [theme.breakpoints.up('tablet')]: {
        gap: SPACES.xxl,
      },
    },
  },
  selectionLabel: (props) => ({
    cursor: props.disabled ? 'auto' : 'pointer',
  }),
  image: ({ isRoundImage }: IStyleProps) => ({
    '&&': {
      [theme.breakpoints.down('tablet')]: {
        width: 50,
        height: 50,

        filter: isRoundImage ? `drop-shadow(0px 2px 10px ${theme.palette.custom.grey})` : 'none',
      },
      [theme.breakpoints.up('tablet')]: {
        width: 80,
        height: 80,
      },

      borderRadius: isRoundImage ? '50%' : 5,
    },
  }),
  imageIconWrapper: ({ isRoundImage }: IStyleProps) => ({
    '&&': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      [theme.breakpoints.down('tablet')]: {
        width: 50,
        height: 50,

        borderRadius: isRoundImage ? '50%' : 5,
        border: `1px dashed ${theme.palette.custom.grey2}`,
      },

      [theme.breakpoints.up('tablet')]: {
        width: 80,
        height: 80,

        borderRadius: isRoundImage ? '50%' : 5,
        border: `2px dashed ${theme.palette.custom.grey2}`,
      },
    },
  }),
  imageIcon: {
    width: 24,
    height: 24,
  },
  selectionText: {
    '&&': {
      fontWeight: FONTS.WEIGHTS.medium,
      fontSize: FONTS.SIZES.m,
      lineHeight: 1.33,

      wordWrap: 'break-word',
      wordBreak: 'break-word',
      whiteSpace: 'normal',
    },
  },
  selectionSubtext: {
    '&&': {
      fontSize: FONTS.SIZES.s,
      lineHeight: 1.33,

      wordWrap: 'break-word',
      wordBreak: 'break-word',
      whiteSpace: 'normal',
    },
  },
  selectionTextDark: {
    '&&': {
      color: theme.palette.custom.darkBlue2,
    },
  },
  selectionTextLight: {
    '&&': {
      color: theme.palette.custom.grey2,
    },
  },
  selectionItemDragIconButton: {
    '&&': {
      fill: theme.palette.custom.grey2,

      '&:disabled': {
        fill: theme.palette.custom.grey,
      },
    },
  },
  selectionItemDragIcon: {
    width: 24,
    height: 24,
    fill: 'inherit',
  },
  errorText: {
    '&&': {
      fontSize: FONTS.SIZES.s,
      marginBottom: -14,
      marginTop: -4,
    },
  },
  switchWrapper: {
    '&&': {
      marginTop: 0,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
  },
  actionsWrapper: {
    '&&': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',

      width: '100%',

      [theme.breakpoints.down('tablet')]: {
        padding: `0 ${SPACES.xs} ${SPACES.xs}`,
      },

      [theme.breakpoints.up('tablet')]: {
        padding: `0 ${SPACES.xxl} ${SPACES.xxl}`,
      },
    },
  },
  actionsButtonWrapper: {
    '&&': {
      display: 'flex',
      alignItems: 'center',
      gap: SPACES.xxxs,
      height: '20px',
    },
  },
  actionsTitle: {
    '&&': {
      fontSize: FONTS.SIZES.default,
    },
  },
}))

export default useStyles
